import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {object} OutOfStockSubscribeRefs
 * @property {HTMLButtonElement} trigger
 * @property {HTMLDialogElement} [subscribeDialog]
 * @property {HTMLButtonElement} [closeButton]
 * @property {HTMLDialogElement} [successDialog]
 * @property {HTMLInputElement} [tagsInput]
 * @property {HTMLFormElement} [submitButton]
 */

/** @extends {Component<OutOfStockSubscribeRefs>} */
class OutOfStockSubscribe extends Component {
  requiredRefs = ['trigger'];

  /** @type {HTMLDialogElement | null} */
  #subscribeDialog = null;

  /** @type {HTMLDialogElement | null} */
  #successDialog = null;

  /** @type {HTMLButtonElement | null} */
  #closeButton = null;

  /** @type {HTMLInputElement | null} */
  #tagsInput = null;

  /** @type {HTMLFormElement | null} */
  #customerForm = null;

  connectedCallback() {
    super.connectedCallback();

    this.#subscribeDialog = this.querySelector('dialog[ref="subscribeDialog"]');
    this.#successDialog = this.querySelector('dialog[ref="successDialog"]');
    this.#closeButton = this.#subscribeDialog?.querySelector('[ref="closeButton"]') ?? null;
    this.#tagsInput = this.#subscribeDialog?.querySelector('[ref="tagsInput"]') ?? null;
    this.#customerForm = this.#subscribeDialog?.querySelector('.oos-subscribe__customer-form') ?? null;

    if (this.#subscribeDialog) document.body.append(this.#subscribeDialog);
    if (this.#successDialog) document.body.append(this.#successDialog);

    this.refs.trigger.addEventListener('click', this.#openDialog);
    this.#closeButton?.addEventListener('click', this.#closeDialog);
    this.#subscribeDialog?.addEventListener('click', this.#handleDialogClick);
    this.#customerForm?.addEventListener('submit', this.#handleSubmit);

    const productForm = this.closest('product-form-component');
    productForm?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.trigger?.removeEventListener('click', this.#openDialog);
    this.#closeButton?.removeEventListener('click', this.#closeDialog);
    this.#subscribeDialog?.removeEventListener('click', this.#handleDialogClick);
    this.#customerForm?.removeEventListener('submit', this.#handleSubmit);

    this.#subscribeDialog?.remove();
    this.#successDialog?.remove();

    const productForm = this.closest('product-form-component');
    productForm?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  #openDialog = () => {
    if (!this.#subscribeDialog || this.#subscribeDialog.open) return;
    this.#subscribeDialog.showModal();
  };

  #closeDialog = () => {
    if (!this.#subscribeDialog?.open) return;
    this.#subscribeDialog.close();
  };

  #handleDialogClick = (event) => {
    if (event.target === this.#subscribeDialog) {
      this.#closeDialog();
    }
  };

  /** @param {SubmitEvent} event */
  #handleSubmit = () => {
    const tagsInput = this.#tagsInput;
    if (!tagsInput) return;

    const currentUrl = window.location.href;
    const baseTags = '缺货订阅';
    const productTitle = this.dataset.productTitle || '';
    const selectedOption = this.#subscribeDialog?.querySelector('input[name="ms02_option"]:checked');
    const ms02Tags = selectedOption ? `,MS02-Option:${selectedOption.value}` : '';

    if (productTitle) {
      tagsInput.value = `${baseTags},Page:${currentUrl},Product:${productTitle},NAS${ms02Tags}`;
    } else {
      tagsInput.value = `${baseTags},Page:${currentUrl},NAS${ms02Tags}`;
    }

    window.setTimeout(() => {
      this.#closeDialog();
      if (this.#successDialog && !this.#successDialog.open) {
        this.#successDialog.showModal();
      }
      window.setTimeout(() => {
        if (this.#successDialog?.open) {
          this.#successDialog.close();
        }
      }, 4500);
    }, 500);
  };

  /** @param {CustomEvent} event */
  #onVariantUpdate = (event) => {
    if (event.detail?.data?.productId !== this.dataset.productId) return;

    const available = event.detail.resource?.available ?? false;
    this.#toggleSubscribe(!available);
  };

  /** @param {boolean} showSubscribe */
  #toggleSubscribe(showSubscribe) {
    this.hidden = !showSubscribe;

    const scope =
      this.closest('.buy-buttons-block') ||
      this.closest('.form-action') ||
      this.closest('.product-sticky-actions') ||
      this.closest('product-form-component')?.parentElement;

    if (!scope) return;

    const addToCart = scope.querySelector('add-to-cart-component');
    const buyButtons = scope.querySelector('.product-form-buttons');
    const quantitySelector = scope.querySelector('.quantity-selector');
    const paymentButton = scope.querySelector('.shopify-payment-button');

    if (addToCart) {
      addToCart.hidden = showSubscribe;
    }

    if (quantitySelector) {
      quantitySelector.hidden = showSubscribe;
    }

    if (paymentButton) {
      paymentButton.hidden = showSubscribe;
    }

    if (buyButtons) {
      buyButtons.hidden = showSubscribe;
    }
  }
}

if (!customElements.get('out-of-stock-subscribe')) {
  customElements.define('out-of-stock-subscribe', OutOfStockSubscribe);
}
